import styles from './ProductTile.module.scss'

// ── Slot sub-components ────────────────────────────────────────────────────────

function Header({ children }) {
  if (!children) return null
  return <div className={styles.header}>{children}</div>
}
Header.displayName = 'ProductTile.Header'

function Content({ children }) {
  if (!children) return null
  return <div className={styles.content}>{children}</div>
}
Content.displayName = 'ProductTile.Content'

function Action({ children }) {
  if (!children) return null
  return <div className={styles.action}>{children}</div>
}
Action.displayName = 'ProductTile.Action'

// ── Backbone ───────────────────────────────────────────────────────────────────

/**
 * ProductTile — CSS backbone for product card layouts.
 *
 * Zero domain logic. The caller owns slot content via the three sub-components:
 * - `ProductTile.Header`  — top slot (e.g. number, investment name, offer tag, favorites button)
 * - `ProductTile.Content` — middle slot (e.g. plan, data grid (status, rooms, area, price))
 * - `ProductTile.Action`  — bottom slot (e.g. CTA)
 *
 * All slots are optional — omitting a slot collapses that area.
 *
 * `variant` controls layout/padding via CSS only. Available variants:
 * - `grid`           — standard card (default): shadow, hover lift
 * - `compact`        — tight, no shadow (dense list/sidebar use)
 * - add more variants as needed (e.g. `tooltip`, `favorites`) without changing the API
 *
 * A11y props (`role`, `tabIndex`, `onKeyDown`, `aria-label`, etc.) are forwarded
 * to the root `<div>`. When `onClick` is provided, `.clickable` class is applied
 * and the tile becomes interactive (cursor + hover effect).
 *
 * @param {object}   props
 * @param {React.ReactNode}  props.children    - Slot sub-components
 * @param {'grid'|'compact'} [props.variant='grid']
 * @param {string}  [props.className]  - Extra class merged onto root element
 * @param {function}[props.onClick]    - Click handler; adds pointer cursor + hover effect
 */
export function ProductTile({ children, variant = 'grid', className, onClick, ...rest }) {
  const variantClass = `variant${variant.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`

  const containerClass = [
    styles.tile,
    styles[variantClass] ?? '',

    onClick ? styles.clickable : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <div className={containerClass} onClick={onClick} {...rest}>
      {children}
    </div>
  )
}

ProductTile.Header  = Header
ProductTile.Content = Content
ProductTile.Action  = Action
